import React from "react";
import { Rocket, Percent, BookOpen, Repeat, Calculator, Search, Flame } from "lucide-react";
import CTAButtonLink from "../CTA/CTAButtonLink.jsx";
import BookmakerBanner from "../ui/BookmakerBanner.jsx";
import offersConfig from "../../config/offersConfig"; // adjust the path as needed
import Seo from "../Seo.jsx";
import mainConfig from "../../config/mainConfig"; // adjust the path as needed

import { VerticalTimeline } from "../GetStarted/GetStartedHelpers.jsx";
import "../Guides/guides.css";
import "../GetStarted/getStarted.css";
import "./nav-pages.css";
import { Link } from "react-router-dom";

// Import your guides config
import guidesConfig from "../../config/guidesConfig";

const GuidesMain = () => {
  const meta = mainConfig.guides?.seo || {};

  // Example timeline data; you can adjust as needed.
  const timelineData = [
    {
      link: "/guides/how-to-read-odds",
      content: (
        <div className="vertical-box-inner">
          <div className="vertical-box-header">
            <h4>
              <Percent /> 1. Understand Odds Formats
            </h4>
          </div>
          <div className="vertical-box-body">
            <p>Learn how to read odds</p>
          </div>
        </div>
      )
    },
    {
      link: "/guides/betting-exchange-guide",
      content: (
        <div className="vertical-box-inner">
          <div className="vertical-box-header">
            <h4>
              <Repeat /> 2. Learn about exchanges
            </h4>
          </div>
          <div className="vertical-box-body">
            <p>Understand betting exchanges and why we need them</p>
          </div>
        </div>
      )
    },
    {
      link: "/guides/lay-bet-guide",
      content: (
        <div className="vertical-box-inner">
          <div className="vertical-box-header">
            <h4>
              <Search /> 3. Learn how to find matches
            </h4>
          </div>
          <div className="vertical-box-body">
            <p>Find the best matches for your bets</p>
          </div>
        </div>
      )
    },
    {
      link: "/calculators/lay-stake-calculator",
      content: (
        <div className="vertical-box-inner">
          <div className="vertical-box-header">
            <h4>
              <Calculator /> 4. Practice Using a Calculator
            </h4>
          </div>
          <div className="vertical-box-body">
            <p>Work through backing and laying a match</p>
          </div>
        </div>
      )
    }
  ];

  // Loop over guidesConfig and filter based on category
  const guidesArray = Object.values(guidesConfig);
  const introGuides = guidesArray.filter(guide => guide.category.includes("intro"));
  const basicGuides = guidesArray.filter(guide => guide.category.includes("basic"));
  const interGuides = guidesArray.filter(guide => guide.category.includes("inter"));

  const advancedGuides = guidesArray.filter(guide => guide.category.includes("advanced"));

  return (
    <div className="guides-content guides-content-pad menu-page">
       <Seo title={meta.title} description={meta.description} url={meta.route}/>

      <h1>Guides</h1>
      <CTAButtonLink 
  to="/get-started/step1"
  text="Understand How Matched Betting Works in 180 Seconds"
  buttonText="Start now"
  icon={Rocket}
  tabText="Popular"
/>

      <div className="content-container mb-32">
        <h2>What Next?</h2>
        <div className="grid-fixed g2 wrap320">
          <div className="col">
            <h3>Get confident with the basics</h3>
            <VerticalTimeline props={{ timelineData }} />
          </div>
          <div className="col">
            <h3>Choose an offer</h3>
            <p>
              These bookies often run straightforward offers, which are great to get you started. Always read the Ts & Cs carefully.
            </p>
            {offersConfig.bookies.map((offer, index) => (
        <BookmakerBanner key={index} {...offer} />
      ))}
      <hr></hr>
                  <h3>Choose an exchange</h3>

      {offersConfig.exchanges.map((offer, index) => (
        <BookmakerBanner key={index} {...offer} />
      ))}
            <Link to="/get-started/step4">
              <button className="next-button">Next</button>
            </Link>
          </div>
        </div>
      </div>

      {/* Render Intro Guides dynamically */}
      <div className="grid-wrap g2">
        <div className="content-container">
          <h3>Intro Guides</h3>
          {introGuides.map((guide, idx) => (
            <Link to={guide.route} className="guide-link guide-link--highlight" key={idx}>
              <div className="guide-main">
                <div className="guide-icon">
                  {guide.sidebar.icon || <BookOpen size={20} />}
                </div>
                <div className="guide-label">{guide.sidebar.label}</div>
              </div>
              {guide.highlight ?
                <div className="guide-highlight">
                  <Flame size={20} color="#fff" />
                </div>
                : <></>
              }
            </Link>
          ))}
        </div>
        
        {/* Render Basic Profit Strategies dynamically */}
        <div className="content-container">
          <h3>Basic Profit Strategies</h3>
          {basicGuides.map((guide, idx) => (
            <Link to={guide.route} className="guide-link guide-link--highlight" key={idx}>
              <div className="guide-main">
                <div className="guide-icon">
                  {guide.sidebar.icon || <BookOpen size={20} />}
                </div>
                <div className="guide-label">{guide.sidebar.label}</div>
              </div>
              {guide.highlight ?
                <div className="guide-highlight">
                  <Flame size={20} color="#fff" />
                </div>
                : <></>
              }
            </Link>
          ))}
        </div>

        <div className="content-container">
          <h3>Next Steps</h3>
          {interGuides.map((guide, idx) => (
            <Link to={guide.route} className="guide-link guide-link--highlight" key={idx}>
              <div className="guide-main">
                <div className="guide-icon">
                  {guide.sidebar.icon || <BookOpen size={20} />}
                </div>
                <div className="guide-label">{guide.sidebar.label}</div>
              </div>
              {guide.highlight ?
                <div className="guide-highlight">
                  <Flame size={20} color="#fff" />
                </div>
                : <></>
              }
            </Link>
          ))}
        </div>

        <div className="content-container">
          <h3>Advanced Profit Strategies</h3>
          {advancedGuides.map((guide, idx) => (
            <Link to={guide.route} className="guide-link guide-link--highlight" key={idx}>
              <div className="guide-main">
                <div className="guide-icon">
                  {guide.sidebar.icon || <BookOpen size={20} />}
                </div>
                <div className="guide-label">{guide.sidebar.label}</div>
              </div>
              {guide.highlight ?
                <div className="guide-highlight">
                  <Flame size={20} color="#fff" />
                </div>
                : <></>
              }
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
};

export default GuidesMain;
